<template>
  <b-card no-body class="w-100">
    <!-- card header -->
    <div slot="header" class="header">
      <div>
        {{ chartTitle }}
        <div class="metric-selector float-right">
          <buttons-select :choices="metric.choices" v-model="metric.value"/>
        </div>
      </div>

    </div>

    <!-- card body -->
    <b-card-body v-if="isEmptyData" class="text-center">
      No data available
    </b-card-body>

    <b-card-body v-else class="card-body">

      <div class="direction-labels">
        <div>Load port</div>
        <div>Discharge port</div>
      </div>

      <vue-apex-charts
        class="dashboard-chart"
        :height="120"
        :width="'100%'"
        :options="chartOptions"
        :series="chartSeries"/>
      <i>This analysis is based on <b>{{statsComputed.voyages}}</b> voyages carrying <b>{{statsComputed.volume}}</b>
        tonnes of cargo</i><br>
        The average total voyage duration was <b>{{Math.round(singleModelVoyage.avgVoyageDurationsDays)}} days</b> and an average speed of <b>{{Math.round(avgSpeed)}} knots</b>.
    </b-card-body>
  </b-card>
</template>

<script>
  import getNested from 'lodash-es/get'
  import isEmpty from 'lodash-es/isEmpty'
  import camelCase from 'lodash-es/camelCase'
  import startCase from 'lodash-es/startCase'
  import ButtonsSelect from '@/components/widgets/ButtonsSelect'
  import VueApexCharts from 'vue-apexcharts'
  import {formatCount, formatVolume,} from '@/shared/number-utils'
  import {formatFloat} from '@/shared/number-utils'
  import {modelVoyagePalette} from '@/shared/constants/colors'
  import { mapGetters } from 'vuex'

  export default {
    name: 'model-voyage-chart',

    components: {
      ButtonsSelect,
      VueApexCharts,
    },

    props: {
      title: {
        type: String,
        default: 'Model Voyage'
      },

      stats: {
        type: Object,
        default: () => null,
      },

      modelVoyages: {
        type: Array,
        default: () => null,
      },

      parametersOrder: {
        type: Array,
        default: () => [
          {parameterName: 'LoadPortDaysWaiting', legendName: 'waiting days (load port)'},
          {parameterName: 'LoadPortDaysBerthed', legendName: 'loading days'},
          {parameterName: 'DaysAtSea', legendName: 'days at sea'},
          {parameterName: 'DischargePortDaysWaiting', legendName: 'waiting days (discharge port)'},
          {parameterName: 'DischargePortDaysBerthed', legendName: 'discharge days'},
        ],
      },
    },

    data() {
      return {
        metric: {
          choices: [
            {text: 'Show Average Values', value: 'avg', fullName: 'average'},
            {text: 'Show Median Values', value: 'median', fullName: 'median'},
          ],
          value: 'avg',
        },
      };
    },

    methods: {
      getFormattedValue(chartValue, data) {
        const {seriesIndex, dataPointIndex} = data;
        const value = getNested(this.chartSeries, `${seriesIndex}.data.${dataPointIndex}`, 0);
        const formattedValue = formatFloat(value, '0,0.0');
        return `${formattedValue} days`;
      },
    },

    computed: {
      ...mapGetters( 'gui', [
          'darkMode',
        ]
      ),

      chartTitle() {
        let metric = 'average'
        if (this.metric.value !== 'avg') {
          metric = 'median'
        }

        return 'Turnaround analysis for selected trade lane (' + metric + ' values)'
      },


      chartSeries() {
        if (!this.modelVoyages) {
          return [];
        }

        return this.parametersOrder.map(parameter => {
          const fieldName = `${this.metric.value}${parameter.parameterName}`;
          const fieldValues = this.modelVoyages.map(voyage => getNested(voyage, fieldName, 0));

          return {
            name: startCase(camelCase(`${this.fullMetricName} ${parameter.legendName}`)),
            data: fieldValues,
          };
        });
      },

      fullMetricName() {
        const currentChoice = this.metric.choices.find(item => item.value === this.metric.value);
        return currentChoice.fullName;
      },

      isEmptyData() {
        return isEmpty(this.modelVoyages);
      },

      chartOptions() {
        return {
          theme: {
            mode: this.darkMode ? 'dark' : 'light',
          },
          colors: modelVoyagePalette,
          chart: {
            background: 'transparent',
            zoom: {enabled: false},
            toolbar: {show: false},
            type: 'bar',
            stacked: true,
            stackType: '100%',
          },
          grid: {
            show: false,
            padding: {
              top: 0,
              bottom: 0,
              left: 0,
              right: 17,
            },
          },
          xaxis: {
            labels: {
              show: false,
            },
            axisBorder: {
              show: false,
            },
            axisTicks: {
              show: false,
            },
          },
          yaxis: {
            show: false,
          },
          plotOptions: {
            bar: {
              horizontal: true,
            },
          },

          dataLabels: {
            enabled: true,
            formatter: this.getFormattedValue.bind(this),
            style: {
              fontSize: '14px',
              fontWeight: '600',
              colors: ['white'],
            },
          },
          tooltip: {
            enabled: true,
            x: {
              formatter: () => '',
            },
            y: {
              formatter: this.getFormattedValue.bind(this),
            },
          }
        };
      },
      statsComputed: function () {
        if (this.stats) {
          let stats = this.stats;
          return {
            ...stats,
            volume: formatVolume(stats.volume),
            voyages: formatCount(stats.voyages),
            uniqueVessels: formatCount(stats.uniqueVessels),
          }
        } else {
          return {};
        }
      },
      singleModelVoyage: function() {
        if (this.modelVoyages[0]) {
          return this.modelVoyages[0];
        } else {
          return {};
        }
      },
      avgSpeed: function() {
        if (this.modelVoyages[0]) {
          let v = this.modelVoyages[0]
          return v.avgDistanceNm / (v.avgDaysAtSea * 24);
        } else {
          return 0;
        }
      }
    },
  }
</script>

<style scoped lang="scss">
  .card-body {
    .metric-selector {
      display: flex;
      justify-content: right;
    }

    .direction-labels {
      margin-bottom: -40px;
      padding: 0 20px 0 20px;
      display: flex;
      justify-content: space-between;
      font-size: 22px;
    }
  }
</style>
